import { useEffect, useRef, useState } from 'react';
import ConnectionManageSurface from '@/components/connections/ConnectionManageSurface';
import { useLanguage } from '@/i18n/LanguageProvider';
import { useLibraryIdentity } from '@/hooks/useLibraryIdentity';
import { supabase } from '@/integrations/supabase/client';
import { MIRROR_FITNESS_CARDS_ENABLED } from '@/lib/featureFlags';
import { prepareFitnessRuntimeAction, confirmFitnessRuntimeAction, type FitnessRuntimeActionReceipt, type PreparedFitnessRuntimeAction } from '@/lib/fitnessRuntimeAction';
import type { FitnessRuntimeActionArgs, FitnessRuntimeEnvelope } from '../../../supabase/functions/_shared/fitnessRuntimeDomain';

export interface FitnessRuntimeActionPrefill {
  ownerId: string; sessionId: string; actionId: string; args: FitnessRuntimeActionArgs; request: FitnessRuntimeEnvelope; isCurrent: () => boolean;
}
interface Props {
  prefill: FitnessRuntimeActionPrefill | null; onClose: () => void;
  onLocalReceipt: (receipt: FitnessRuntimeActionReceipt, isCurrent: () => boolean) => Promise<boolean>;
}
const button = 'min-h-11 rounded-xl border border-border px-3 py-2 text-sm disabled:opacity-50';
const errorKey = (code: string) => code === 'fitness_runtime_disabled' ? 'fitnessRuntimePreview.off' : 'fitnessRuntimeAction.' + (code === 'invalid_session' ? 'invalidSession' : code === 'fitness_runtime_busy' ? 'busy' : code === 'outcome_unknown' ? 'unknown' : code === 'ack_pending' ? 'ackPending' : 'changed');

export function FitnessRuntimeActionModal(props: Props) {
  const identity = useLibraryIdentity(), prefill = props.prefill;
  if (!prefill || !identity.ready || identity.userId !== prefill.ownerId || !prefill.isCurrent()) return null;
  return <RuntimeReview key={[prefill.ownerId, identity.generation, prefill.sessionId, prefill.actionId].join(':')} {...props} prefill={prefill} />;
}
function RuntimeReview({ prefill: incoming, onClose, onLocalReceipt }: Props & { prefill: FitnessRuntimeActionPrefill }) {
  const { t, tf } = useLanguage(), epoch = useRef(0), locked = useRef(false);
  const [prefill] = useState(() => ({ ...incoming, args: structuredClone(incoming.args) }));
  const [origin] = useState(() => prefill.isCurrent), [prepared, setPrepared] = useState<PreparedFitnessRuntimeAction | null>(null);
  const [saved, setSaved] = useState<FitnessRuntimeActionReceipt | null>(null), [busy, setBusy] = useState(false), [error, setError] = useState<string | null>(null);
  const close = () => { epoch.current++; onClose(); };
  useEffect(() => {
    const run = epoch.current, current = () => run === epoch.current && origin();
    void prepareFitnessRuntimeAction({ ...prefill, auth: supabase.auth, isCurrent: current }).then(review => {
      if (current()) setPrepared(review);
    }).catch(cause => { if (current()) setError(cause instanceof Error ? cause.message : 'outcome_unknown'); });
    const lifetime = epoch; return () => { lifetime.current++; };
  }, [origin, prefill]);
  const confirm = async () => {
    if (locked.current || !saved && (!prepared || !!error || !MIRROR_FITNESS_CARDS_ENABLED)) return;
    const run = epoch.current, current = () => run === epoch.current && origin();
    if (!current()) return;
    locked.current = true; setBusy(true); setError(null);
    try {
      // Even a known receipt retry refreshes the same identity. It only retries
      // the local React acknowledgement, never a controller command.
      const receipt = await confirmFitnessRuntimeAction(prepared!);
      if (!current()) return;
      setSaved(receipt);
      if (!await onLocalReceipt(receipt, current) || !current()) throw new Error('ack_pending');
      close();
    } catch (cause) { if (current()) setError(cause instanceof Error ? cause.message : 'outcome_unknown'); }
    finally { locked.current = false; if (current()) setBusy(false); }
  };
  const summary = prepared?.summary;
  return <ConnectionManageSurface open onOpenChange={open => { if (!open) close(); }} title={t('fitnessRuntimeAction.title')} description={t('fitnessRuntimeAction.localOnly')} closeLabel={t('common.close')} brand={<span aria-hidden="true">🏋️</span>}>
    <div className="space-y-4 p-1">
      <p>{t('fitnessRuntimeAction.preserve')}</p>
      {error && <p role="alert">{t(errorKey(error))}</p>}
      {!prepared && !error && <p role="status">{t('common.loading')}</p>}
      {summary && <dl className="space-y-3 text-sm">
        <div><dt className="text-muted-foreground">{t('fitnessRuntimeAction.workout')}</dt><dd className="whitespace-pre-wrap break-words">{summary.workoutName ?? t('fitnessAction.none')}</dd></div>
        <div><dt className="text-muted-foreground">{t('fitnessRuntimeAction.action')}</dt><dd>{t('fitnessRuntime.' + prepared.action)}</dd></div>
        <div><dt className="text-muted-foreground">{t('fitnessRuntime.' + (summary.paused ? 'paused' : 'running'))}</dt><dd>{tf('fitnessRuntimeAction.position', { exercise: summary.currentExerciseIndex + 1, set: summary.currentSet })}</dd></div>
      </dl>}
      {saved && <p role="status">{t('fitnessRuntimeAction.' + (saved.action === 'pause' ? 'localPaused' : 'localResumed'))}</p>}
      {prepared && <button type="button" className={button + ' bg-primary text-primary-foreground font-semibold'} disabled={busy || !saved && (!!error || !MIRROR_FITNESS_CARDS_ENABLED)} onClick={() => void confirm()}>{t(busy ? 'common.loading' : saved ? 'common.retry' : prepared.action === 'pause' ? 'fitnessRuntimeAction.confirmPause' : 'fitnessRuntimeAction.confirmResume')}</button>}
      <button type="button" className={button} onClick={close}>{t('common.cancel')}</button>
    </div>
  </ConnectionManageSurface>;
}
